import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scrolls the window, document, and layout scroll containers (like .shell__main)
 * to the top smoothly whenever a new route or page is visited.
 */
export default function ScrollToTop() {
  const { pathname, search, hash, key } = useLocation();

  useEffect(() => {
    // Disable automatic browser scroll restoration to ensure smooth scroll is not interrupted
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    // If navigating with an in-page anchor (#id), scroll to that element smoothly
    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }

    const scrollToTop = () => {
      // 1. Scroll window and document elements
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });

      if (document.documentElement) {
        document.documentElement.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      }

      if (document.body) {
        document.body.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      }

      // 2. Scroll the main shell container and any bespoke scroll containers
      const scrollContainers = document.querySelectorAll(
        '.shell__main, [data-scroll-container], .signin-form-side'
      );
      scrollContainers.forEach((container) => {
        container.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      });
    };

    scrollToTop();

    // Re-trigger on next animation frame to catch layout changes or dynamic page height updates
    const rafId = requestAnimationFrame(() => {
      scrollToTop();
    });

    return () => cancelAnimationFrame(rafId);
  }, [pathname, search, hash, key]);

  return null;
}
